import { download32 } from "@esri/calcite-ui-icons";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormattedMessage } from "react-intl";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../components/AuthProvider";
import Message from "../components/Message";
import Warning from "../components/Warning";
import appconfig from "../config.json";

function DownloadPage({
    portalUrl,
    mediaUrl,
    product,
    onProductNameRetrieved
}: {
    portalUrl: string;
    mediaUrl: string;
    product?: any;
    onProductNameRetrieved: (productName: string) => void;
}) {
    // Authentication
    const { token, isSignedIn, appAuthId, authUrlPath, autoAuthorize } = useAuth();

    const { t } = useTranslation("common");

    const productId =
            product !== undefined && product !== null && product.id !== undefined
                ? product.id
                : // eslint-disable-next-line react-hooks/rules-of-hooks
                  useParams<Record<string, string | undefined>>().productId,
        oAuthUrl: string = `${portalUrl}/oauth2/authorize?client_id=${appAuthId}&response_type=token&redirect_uri=${encodeURIComponent(
            window.location.href
        )}`,
        downloadPath: string =
            product !== undefined && product !== null && product.downloadPath !== undefined
                ? product.downloadPath
                : "/download-file/{filename}", // Was '/download-file/{product}/{filename}' but product ID is part of pathname from service
        listPath: string = "/list-files/{product}";
    const [working, setWorking] = useState<boolean>(false);
    const [productPurchase, setProductPurchase] = useState<any>(null);
    const [productDetails, setProductDetails] = useState<any>(null);
    const [productMedia, setProductMedia] = useState<any[]>([]);
    const [portalSelf, setPortalSelf] = useState<any>(null);
    const [syncError, setSyncError] = useState<any>(null);

    useEffect(() => {
        async function loadProductDetails() {
            try {
                setWorking(true);
                // Hard-wired product information - no need to access web/cloud resources
                if (
                    product !== undefined &&
                    product !== null &&
                    product.anonymous !== undefined &&
                    product.anonymous === true
                ) {
                    setProductDetails({
                        ...product,
                        anonymous: true
                    });
                    setProductPurchase({});
                    try {
                        const productMediaUrl: string = `${mediaUrl}${listPath}`.replace(
                                "{product}",
                                productId || ""
                            ),
                            mediaList: any = await fetch(`${productMediaUrl}?f=json`).then(
                                (rsp) => {
                                    return rsp.json();
                                }
                            );
                        setProductMedia(mediaList);
                    } catch (mediaEx) {
                        setProductMedia([]);
                    }
                }
                // Standard cloud-based mechanism - product information comes from Marketplace etc.
                else {
                    const productSet: any = await fetch(
                        `${portalUrl}/content/listings/${productId}?f=json`
                    ).then((rsp) => {
                        return rsp.json();
                    });
                    setProductDetails({
                        ...productSet,
                        anonymous: false
                    });
                    if (isSignedIn) {
                        const self: any = await fetch(
                            `${portalUrl}/portals/self?f=json&token=${token}`
                        ).then((rsp) => {
                            return rsp.json();
                        });
                        if (self.error !== undefined) throw new Error(self.error.message);
                        setPortalSelf(self);
                        const bought: any = await fetch(
                                `${portalUrl}/accounts/${self.id}/purchases?f=json&status=active&token=${token}`
                            ).then((rsp) => {
                                return rsp.json();
                            }),
                            purchase: any =
                                bought.purchases !== undefined
                                    ? bought.purchases.find(
                                          (p: { provision: { itemId: undefined } | undefined }) =>
                                              p.provision !== undefined &&
                                              p.provision.itemId !== undefined &&
                                              p.provision.itemId === productId
                                      )
                                    : {};
                        if (purchase === undefined) {
                            setSyncError({
                                code: "NoPurchase",
                                message: (
                                    <FormattedMessage
                                        id="license.error.nopurchase.message"
                                        defaultMessage="{org} has not purchased {product}"
                                        values={{
                                            org: <strong className="org-name">{self.name}</strong>,
                                            product: (
                                                <strong className="product-name">
                                                    {productSet.title}
                                                </strong>
                                            )
                                        }}
                                    />
                                )
                            });
                        } else {
                            setProductPurchase(purchase);
                            const productMediaUrl: string = `${mediaUrl}${listPath}`.replace(
                                    "{product}",
                                    productId || ""
                                ),
                                mediaList: any = await fetch(
                                    `${productMediaUrl}?f=json&token=${token}`
                                ).then((rsp) => {
                                    return rsp.json();
                                });
                            setProductMedia(mediaList);
                        }
                    } else if (autoAuthorize) window.location.href = oAuthUrl;
                }
                setWorking(false);
            } catch (syncErr) {
                setSyncError(syncErr);
                setWorking(false);
            }
        }
        loadProductDetails();
    }, [isSignedIn, portalUrl, appAuthId, productId, token, product, mediaUrl]);

    let productName: string = "?";
    if (productDetails !== undefined && productDetails !== null)
        productName =
            t(`products.${productDetails.itemId ?? productDetails.id}`) ?? productDetails.title;

    // Call the callback function with the product name
    onProductNameRetrieved(productName);

    // let productMismatch: boolean = false;
    // if (portalSelf !== null && portalSelf.appInfo !== undefined)
    //     productMismatch = productId !== portalSelf.appInfo.itemId;

    const anonymousProduct =
            (product !== undefined &&
                product !== null &&
                product.anonymous !== undefined &&
                product.anonymous === true) ||
            (productDetails !== undefined &&
                productDetails !== null &&
                productDetails.anonymous !== undefined &&
                productDetails.anonymous === true),
        productDownloads: any[] = productMedia.filter((m) => m.pathname.indexOf("media/") > 0),
        productDocs: any[] = productMedia.filter((m) => m.pathname.indexOf("docs/") > 0),
        productMediaUrl =
            anonymousProduct &&
            product !== undefined &&
            product !== null &&
            product.mediaUrl !== undefined
                ? product.mediaUrl
                : mediaUrl;
    productDownloads.sort((a, b) => {
        return b.filename.localeCompare(a.filename);
    });

    interface Download {
        href?: string;
        version?: string;
        filename?: string;
        pathname?: string;
        name: string;
        type: string;
        versionLabel?: string;
    }

    interface ProductDetails {
        itemId: string;
    }

    const groupByMajorMinor = (downloads: Download[]): Record<string, Download[]> => {
        const grouped: Record<string, Download[]> = {};
        downloads.splice(1).forEach((download) => {
            const versionMatch =
                download.version ||
                download.filename?.match(
                    /^[a-zA-Z0-9-_]+v([0-9]+\.[0-9]+\.[0-9]+)\.(exe|zip)$/
                )?.[1];
            if (versionMatch) {
                const majorMinor = versionMatch.split(".").slice(0, 2).join(".");
                grouped[majorMinor] = grouped[majorMinor] || [];
                grouped[majorMinor].push(download);
            }
        });

        const sortedGrouped: Record<string, Download[]> = {};
        // Sort keys in descending order based on major and minor version numbers
        Object.keys(grouped)
            .sort((a, b) => {
                const aParts = a.split(".").map(Number);
                const bParts = b.split(".").map(Number);
                return bParts[0] - aParts[0] || bParts[1] - aParts[1];
            })
            .forEach((key) => {
                // Sort downloads within the same major.minor group by full version number in descending order
                sortedGrouped[key] = grouped[key].sort((a, b) => {
                    const aVersionParts = (a.version || "").split(".").map(Number);
                    const bVersionParts = (b.version || "").split(".").map(Number);
                    return (
                        bVersionParts[0] - aVersionParts[0] ||
                        bVersionParts[1] - aVersionParts[1] ||
                        bVersionParts[2] - aVersionParts[2]
                    );
                });
            });

        return sortedGrouped;
    };

    interface FilterDownloadsParams {
        groupedDownloads: Record<string, Download[]>;
        majorVersion: string;
    }

    function filterDownloadsGroupByMajorVersion({
        groupedDownloads,
        majorVersion
    }: FilterDownloadsParams): Record<string, Download[]> {
        const filteredDownloads: Record<string, Download[]> = {};

        Object.keys(groupedDownloads).forEach((key) => {
            const downloads = groupedDownloads[key];
            const majorMinorParts = key.split(".");
            const major = parseInt(majorMinorParts[0], 10);
            const minor = parseInt(majorMinorParts[1], 10);
            const [majorVersionMajor, majorVersionMinor] = majorVersion.split(".").map(Number);

            if (
                major > majorVersionMajor ||
                (major === majorVersionMajor && minor >= majorVersionMinor)
            ) {
                filteredDownloads[key] = downloads;
            }
        });

        return filteredDownloads;
    }

    interface DownloadInfo {
        href: string;
        name: string;
        version: string;
        type: string;
        label: string;
    }

    const getDownloadInfo = (
        download: Download,
        productMediaUrl: string,
        downloadPath: string,
        productDetails: ProductDetails
    ): DownloadInfo => {
        const href: string =
            download.href !== undefined
                ? download.href
                : `${productMediaUrl}${downloadPath}`
                      .replace("{product}", productDetails.itemId)
                      .replace("{filename}", download.pathname ?? "");

        const dname: string[] = download.name.split(",");
        const dversion: string =
            download.version !== undefined
                ? download.version
                : download.filename !== undefined &&
                  /^[a-zA-Z0-9-_]+v([0-9]+\.[0-9]+\.[0-9]+)\.(exe|zip)$/.test(download.filename)
                ? download.filename.match(
                      /^[a-zA-Z0-9-_]+v([0-9]+\.[0-9]+\.[0-9]+)\.(exe|zip)$/
                  )?.[1] ?? ""
                : dname[1].substring(0, dname[1].length - 6);
        const dlabel: string = download.versionLabel ?? dversion;
        const dtype: string = dname[1].substring(dname[1].length - 4, dname[1].length - 1);

        return {
            href,
            name: dname[0],
            version: dversion,
            type: dtype,
            label: dlabel
        };
    };

    // Grouping the productDownloads by major and minor version
    const allDownloadsGrouped = groupByMajorMinor(productDownloads);
    const supportedDownloadsGrouped = filterDownloadsGroupByMajorVersion({
        groupedDownloads: allDownloadsGrouped,
        majorVersion: "8.2"
    });

    interface DownloadLinkProps {
        filename: string;
        url: string;
    }

    const DownloadLink = ({ filename, url }: DownloadLinkProps) => {
        const handleDownload = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            event.preventDefault(); // Prevent the default anchor link behavior

            // Show confirmation dialog
            if (
                window.confirm(
                    "This is not the latest version, are you sure you want to download this version?"
                )
            ) {
                // Create a new anchor element and trigger the download
                const link = document.createElement("a");
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        };

        return (
            <a href={url} onClick={handleDownload} className="text-sm text-gray-700 underline">
                {filename}
            </a>
        );
    };

    const latest = productDownloads[0],
        latestInfo = latest
            ? getDownloadInfo(latest, productMediaUrl, downloadPath, productDetails)
            : undefined;

    return (
        <div className="download-page mx-auto">
            {!isSignedIn && !anonymousProduct && (
                <div className="purchase">
                    <div className="warning-message">
                        <div className="px-4 pt-12 pb-12">
                            <div className="sm:flex sm:items-start sm:justify-center">
                                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                                    <h3
                                        className="text-lg leading-6 font-medium text-gray-900"
                                        id="modal-headline"
                                    >
                                        <FormattedMessage
                                            id="downloads.warning.anonymous.message"
                                            defaultMessage="Please sign in to your ArcGIS Online account"
                                        />
                                    </h3>
                                    <div className="mt-2">
                                        <p className="text-gray-500">
                                            <FormattedMessage
                                                id="downloads.warning.anonymous.message"
                                                defaultMessage="This step is required to validate your purchase of {productName}."
                                                values={{
                                                    productName: (
                                                        <strong className="product-name">
                                                            {productName}
                                                        </strong>
                                                    )
                                                }}
                                            />
                                        </p>
                                    </div>
                                    <div className="pt-5 text-right flex">
                                        <a
                                            href={oAuthUrl}
                                            className="px-4 py-2 bg-[#0079c1] hover:bg-[#005e95] text-white flex items-center gap-2 transition-colors duration-300"
                                        >
                                            <FormattedMessage
                                                id="downloads.warning.anonymous.button.signIn"
                                                defaultMessage="Sign in"
                                            />
                                        </a>
                                        <Link
                                            to={token ? `/${authUrlPath}` : "/"}
                                            className="mt-3 inline-flex w-full justify-center border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                        >
                                            <FormattedMessage
                                                id="downloads.form.button.cancel"
                                                defaultMessage="Cancel"
                                            />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {syncError !== undefined && syncError !== null && (
                <Warning
                    syncError={syncError}
                    portalUrl={portalUrl}
                    productDetails={productDetails}
                />
            )}
            {productDetails !== null && <div className="listing"></div>}
            {productDetails !== null &&
            (anonymousProduct || (isSignedIn && productPurchase !== null)) ? (
                <div className="w-100 px-8 md:px-auto">
                    <div className="max-w-screen-xl mx-auto h-16 mx-auto md:px-4 container">
                        <div className="space-y-8 lg:space-y-0 grid lg:grid-cols-12 max-w-screen-xl py-8 mx-auto lg:gap-8 xl:gap-12 lg:py-12 mx-auto mb-10">
                            <div className="col-start-1 col-span-7">
                                <h2 className="mb-4 font-[var(--calcite-font-weight-normal)] text-[var(--calcite-color-text-1)] text-[2rem]">
                                    {productName}
                                </h2>
                                <p className="text-gray-700 mb-4">
                                    {productName} is an easy-to-install distribution containing all
                                    the files and media required to get started.
                                </p>
                                <p className="text-gray-700 mb-4">
                                    For further guidance, check out our{" "}
                                    <a
                                        className="underline break-words"
                                        href={
                                            productId === appconfig.productIds[1]
                                                ? "https://help.une.esriuk.com/architectural-considerations/"
                                                : productId === appconfig.productIds[0]
                                                ? "https://help.sweet.esriuk.com/architectural-considerations/"
                                                : productDetails.url
                                        }
                                        target="helpWindow"
                                    >
                                        getting started help documents
                                    </a>
                                    .
                                </p>

                                <div className="bg-white shadow-lg p-6">
                                    <p className="font-medium text-xl text-gray-700 mb-2">
                                        {`Latest Version${
                                            latestInfo
                                                ? ` (${
                                                      latest.versionLabel ??
                                                      latestInfo.version
                                                          .split(".")
                                                          .slice(0, 2)
                                                          .join(".")
                                                  })`
                                                : ""
                                        }`}
                                    </p>

                                    <p className="mb-4">
                                        Get the official latest version of {productName}.
                                    </p>
                                    {!working && latestInfo && (
                                        <>
                                            <div className="download-latest-version-card flex items-center">
                                                <a
                                                    href={`${latestInfo.href}?token=${token}`}
                                                    download={latestInfo.name}
                                                    className="inline-flex px-5 py-2.5 bg-[#0079c1] hover:bg-[#005e95] text-white flex items-center gap-2 transition-colors duration-300"
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 24 24"
                                                        className="fill-current w-[24px] h-[24px]"
                                                    >
                                                        <path d="M24 12a5 5 0 0 1-5 5h-2v-1h2a3.99 3.99 0 0 0 .623-7.934l-.79-.124-.052-.798a5.293 5.293 0 0 0-10.214-1.57L8.17 6.59l-.977-.483A2.277 2.277 0 0 0 6.19 5.87a2.18 2.18 0 0 0-1.167.339 2.206 2.206 0 0 0-.98 1.395l-.113.505-.476.2A4 4 0 0 0 5 16h3v1H5a5 5 0 0 1-1.934-9.611 3.21 3.21 0 0 1 1.422-2.025 3.17 3.17 0 0 1 1.702-.493 3.268 3.268 0 0 1 1.446.34 6.293 6.293 0 0 1 12.143 1.867A4.988 4.988 0 0 1 24 12zm-11-1h-1v10.292l-2.646-2.646-.707.707 3.854 3.854 3.853-3.852-.707-.707L13 21.294z" />
                                                        <path fill="none" d="M0 0h24v24H0z" />
                                                    </svg>
                                                    <>{t("common.downloadlatest")}</>
                                                </a>

                                                <span className="ml-4 justify-center inline-flex items-center rounded-full bg-[var(--calcite-color-foreground-2)] px-2.5 pt-1 pb-0.5 text-s text-[var(--calcite-color-text-1)]">
                                                    {latest.filename}
                                                </span>
                                            </div>

                                            <div className="mt-6 inline-block">
                                                <a
                                                    href={
                                                        productId === appconfig.productIds[1]
                                                            ? "https://help.une.esriuk.com/release-notes-une/"
                                                            : productId === appconfig.productIds[0]
                                                            ? "https://help.sweet.esriuk.com/release-notes-sweet/"
                                                            : productDetails.url
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="flex items-center space-x-1.5 underline inline-flex"
                                                >
                                                    <span>Read release notes</span>
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 16 16"
                                                        className="fill-current w-[16px] h-[16px]"
                                                    >
                                                        <path d="M4 6h9v1H4zm0 4h9V9H4zm11 6H2V0h9.4L15 3.6zM14 5h-4V1H3v14h11zm0-1.31L11.31 1H11v3h3zM4 13h7v-1H4z" />
                                                        <path fill="none" d="M0 0h16v16H0z" />
                                                    </svg>
                                                </a>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="col-span-5">
                                <p className="text-gray-700 mb-2">
                                    <FormattedMessage
                                        id="downloads.form.instructions"
                                        defaultMessage="Looking for an older version of {product}? While we recommend our latest release for the best experience, we provide access to previous versions here."
                                        values={{
                                            product: (
                                                <strong className="product-name">
                                                    {productName}
                                                </strong>
                                            )
                                        }}
                                    />
                                </p>
                                <p className="text-gray-700 mb-6">
                                    For more information about product versions, release dates and
                                    availability{" "}
                                    <a
                                        href={
                                            productId === appconfig.productIds[1]
                                                ? "https://www.esriuk.com/en-gb/support/esri-uk-products/une-for-arcgis#product-support"
                                                : productId === appconfig.productIds[0]
                                                ? "https://www.esriuk.com/en-gb/support/esri-uk-products/sweet#product-support"
                                                : productDetails.url
                                        }
                                        target="_blank"
                                        rel="noreferrer"
                                        className="underline"
                                    >
                                        please visit our technical support pages
                                    </a>
                                    .
                                </p>

                                <p className="hidden text-gray-700 mb-2">
                                    <FormattedMessage
                                        id="downloads.form.instructions"
                                        defaultMessage="For more information on product versions, release dates, and availability,
                                       {link}."
                                        values={{
                                            link: (
                                                <a
                                                    className="underline"
                                                    href={
                                                        productId === appconfig.productIds[1]
                                                            ? "https://unehelp.une.esriuk.com/builder/#/help/deployment/arcgisent"
                                                            : productId === appconfig.productIds[0]
                                                            ? "https://www.sweet.esriuk.com/builder/#/help/deployment/arcgisent"
                                                            : productDetails.url
                                                    }
                                                    target="helpWindow"
                                                >
                                                    <FormattedMessage
                                                        id="downloads.form.instructions.link"
                                                        defaultMessage="here"
                                                    />
                                                </a>
                                            )
                                        }}
                                    />
                                </p>

                                <h3 className="font-medium text-xl text-gray-700 mb-2">
                                    <>{t("download.headline")}</>
                                </h3>

                                {working && <Message />}

                                {!working && (
                                    <>
                                        <div className="mt-2 overflow-hidden sm:-mx-6 md:mx-0 divide-y">
                                            {Object.keys(supportedDownloadsGrouped).length > 0 ? (
                                                Object.entries(supportedDownloadsGrouped).map(
                                                    ([version, downloads]) => (
                                                        <details
                                                            className="hover:cursor-pointer"
                                                            key={version}
                                                        >
                                                            <summary className="p-3">
                                                                Version {version}
                                                            </summary>

                                                            <ul className="divide-y divide-gray-100">
                                                                {downloads.map((download, i) => {
                                                                    const downloadInfo =
                                                                        getDownloadInfo(
                                                                            download,
                                                                            productMediaUrl,
                                                                            downloadPath,
                                                                            productDetails
                                                                        );
                                                                    return (
                                                                        <li
                                                                            key={i}
                                                                            className="flex justify-between gap-x-6 py-2 first:pt-0 text-sm pl-6 cursor-default"
                                                                        >
                                                                            <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                                                                                {downloadInfo.label}
                                                                            </span>

                                                                            <div className="flex min-w-0 gap-x-4">
                                                                                <span className="inline-flex items-center rounded-md bg-yellow-400/5 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20">
                                                                                    Build{" "}
                                                                                    {
                                                                                        downloadInfo.version
                                                                                    }
                                                                                </span>
                                                                            </div>

                                                                            <div className="shrink-0 sm:flex sm:flex-col sm:items-end">
                                                                                {download.filename && (
                                                                                    <DownloadLink
                                                                                        filename={
                                                                                            download.filename
                                                                                        }
                                                                                        url={`${downloadInfo.href}?token=${token}`}
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </details>
                                                    )
                                                )
                                            ) : (
                                                <div>
                                                    <tr>
                                                        <td
                                                            colSpan={4}
                                                            className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6"
                                                        >
                                                            No downloadable media available
                                                        </td>
                                                    </tr>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                {productDocs.length > 0 && (
                                    <div className="row">
                                        <div className="col col-md-4">
                                            <span className="control-label">
                                                <FormattedMessage
                                                    id="downloads.form.label.documents"
                                                    defaultMessage="Documents"
                                                />
                                            </span>
                                        </div>
                                        <div className="col col-md-8">
                                            <ul className="resource-list documentation-list">
                                                {productDocs.length > 0 ? (
                                                    productDocs.map((m, i) => {
                                                        const href: string =
                                                            m.href !== undefined
                                                                ? m.href
                                                                : `${productMediaUrl}${downloadPath}`
                                                                      .replace(
                                                                          "{product}",
                                                                          productDetails.itemId
                                                                      )
                                                                      .replace(
                                                                          "{filename}",
                                                                          m.filename
                                                                      );
                                                        return (
                                                            <li key={i}>
                                                                <a
                                                                    href={`${href}?token=${token}`}
                                                                    className="media-link"
                                                                >
                                                                    <svg
                                                                        className="btn-icon"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        viewBox="0 0 32 32"
                                                                    >
                                                                        <path d={download32} />
                                                                    </svg>
                                                                    {m.name}
                                                                </a>
                                                            </li>
                                                        );
                                                    })
                                                ) : (
                                                    <li className="font-light text-gray-500">
                                                        No downloadable media available
                                                    </li>
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export default DownloadPage;
